import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Lock,
  LogOut,
  Settings as SettingsIcon,
  Inbox,
  ListChecks,
  KeyRound,
  Webhook,
  Sparkles,
  Layers,
  BadgeCheck,
  Eye,
  EyeOff,
  ChartNoAxesCombined,
  UserRound,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useAdminAuth } from "@/context/AdminAuth";
import { adminVerify } from "@/lib/api";
import { LOGIN, LOGOUT, STORE } from "@/constants/testIds";

const toolbarLink = ({ isActive }) =>
  [
    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
    isActive
      ? "bg-[var(--ibp-navy-950)] text-white"
      : "text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.06)]",
  ].join(" ");

function AdminToolbar() {
  const { logout } = useAdminAuth();
  return (
    <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-3 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-1.5">
        <NavLink to="/settings" className={toolbarLink} data-testid={STORE.adminNavSettings}>
          <SettingsIcon className="h-4 w-4" /> Settings
        </NavLink>
        <NavLink to="/admin/inbox" className={toolbarLink} data-testid={STORE.adminNavInbox}>
          <Inbox className="h-4 w-4" /> Inbox
        </NavLink>
        <NavLink to="/admin/questions" className={toolbarLink} data-testid={STORE.adminNavQuestions}>
          <ListChecks className="h-4 w-4" /> Questions
        </NavLink>
        <NavLink to="/admin/students" className={toolbarLink}>
          <UserRound className="h-4 w-4" /> Students
        </NavLink>
        <NavLink to="/admin/live-review" className={toolbarLink}>
          <CalendarDays className="h-4 w-4" /> Live review
        </NavLink>
        <NavLink to="/admin/access-codes" className={toolbarLink} data-testid={STORE.adminNavAccessCodes}>
          <KeyRound className="h-4 w-4" /> Legacy codes
        </NavLink>
        <NavLink to="/admin/webhook-deliveries" className={toolbarLink} data-testid={STORE.adminNavWebhooks}>
          <Webhook className="h-4 w-4" /> Webhooks
        </NavLink>
        <NavLink to="/admin/analytics" className={toolbarLink}>
          <ChartNoAxesCombined className="h-4 w-4" /> Analytics
        </NavLink>
        <NavLink to="/admin/tutor-usage" className={toolbarLink} data-testid={STORE.adminNavTutorUsage}>
          <Sparkles className="h-4 w-4" /> Tutor usage
        </NavLink>
        <NavLink to="/admin/flashcards" className={toolbarLink} data-testid={STORE.adminNavFlashcards}>
          <Layers className="h-4 w-4" /> Word Bank
        </NavLink>
        <NavLink to="/admin/quality" className={toolbarLink}>
          <BadgeCheck className="h-4 w-4" /> Quality
        </NavLink>
      </div>
      <button
        type="button"
        data-testid={LOGOUT.button}
        onClick={() => {
          logout();
          toast.success("Logged out");
        }}
        className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-[#9b2c2c] transition-colors hover:bg-[color:rgba(155,44,44,0.08)]"
      >
        <LogOut className="h-4 w-4" /> Log out
      </button>
    </div>
  );
}

function LoginCard({ onSubmit, password, setPassword, submitting }) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-8">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]">
          <Lock className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-lg font-semibold text-[var(--ibp-ink)]">Admin sign in</h3>
          <p className="text-sm text-[color:rgba(11,18,32,0.6)]">
            Enter the admin password to continue.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-6">
        <div className="flex items-center justify-between">
          <Label htmlFor="admin-password" className="text-sm font-medium text-[var(--ibp-ink)]">
            Password
          </Label>
          <span
            data-testid="admin-password-charcount"
            className="text-xs text-[color:rgba(11,18,32,0.45)]"
          >
            {password.length} character{password.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="relative mt-1.5">
          <Input
            id="admin-password"
            type={showPassword ? "text" : "password"}
            autoFocus
            autoComplete="current-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            data-testid={LOGIN.passwordInput}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="h-11 rounded-xl pr-11"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            title={showPassword ? "Hide password" : "Show password"}
            data-testid="admin-password-toggle"
            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-[color:rgba(11,18,32,0.5)] transition-colors hover:text-[var(--ibp-ink)]"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <button
          type="submit"
          data-testid={LOGIN.submitButton}
          disabled={submitting || !password}
          className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.18)] transition-colors hover:bg-[var(--ibp-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)] focus-visible:ring-offset-2 disabled:opacity-60"
        >
          {submitting ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}

export default function AdminGate({ children }) {
  const { isAuthed, login, logout } = useAdminAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(isAuthed);

  // Validate any stored token once on mount; drop it if expired/invalid.
  useEffect(() => {
    let active = true;
    if (isAuthed) {
      adminVerify()
        .then(() => {
          if (active) setVerifying(false);
        })
        .catch(() => {
          if (active) {
            logout();
            setVerifying(false);
          }
        });
    } else {
      setVerifying(false);
    }
    return () => {
      active = false;
    };
  }, [isAuthed, logout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(password.trim());
      toast.success("Welcome back");
      setPassword("");
    } catch (err) {
      toast.error("Incorrect password", {
        description: "Double-check for extra spaces, then try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isAuthed && verifying) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-[color:rgba(11,18,32,0.6)]">
        Checking your session...
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <LoginCard
        onSubmit={handleSubmit}
        password={password}
        setPassword={setPassword}
        submitting={submitting}
      />
    );
  }

  return (
    <>
      <AdminToolbar />
      {children}
    </>
  );
}
